import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, MailCheck } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { FORGOT_PASSWORD } from "@/context/Api";

const ForgotPassword = () => {
  const { apiRequest } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const target = email.trim().toLowerCase();
    if (!target) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await FORGOT_PASSWORD({ email: target });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07070c] text-white font-body">
      <Nav />
      <div className="relative min-h-screen flex items-center justify-center px-6 noise overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full blur-[130px] opacity-40 bg-[#4d4479]" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="forgot-password-card"
          className="relative z-10 w-full max-w-md rounded-2xl border border-[#3d414f] bg-[#0a0a12]/90 backdrop-blur-xl p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <span className="grid h-10 w-10 place-items-center rounded-full bg-[#16fcfb] text-[#07070c]"><KeyRound size={18} /></span>
            <span className="font-mono2 text-xs uppercase tracking-[0.25em] text-[#16fcfb]">Reset Password</span>
          </div>
          <h1 className="font-display font-700 text-3xl text-white">Forgot your password?</h1>
          <p className="mt-2 font-body text-sm text-white/60">
            Enter your account email and we'll send you a link to reset it.
          </p>

          {sent ? (
            <div data-testid="forgot-password-sent" className="mt-8 flex flex-col items-center text-center">
              <MailCheck size={48} className="text-[#16fcfb]" />
              <p className="mt-5 font-body text-sm text-white/80 max-w-sm">
                If an account exists for <span className="text-[#16fcfb]">{email.trim().toLowerCase()}</span>, a
                password reset link has been sent. Please check your inbox and Spam / Promotions folder.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="forgot-email" className="font-mono2 text-[11px] uppercase tracking-[0.12em] text-[#97b8ff]">Email</Label>
                <Input id="forgot-email" data-testid="forgot-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                  className="bg-[#07070c] border-[#3d414f] text-white placeholder:text-white/30 focus-visible:ring-[#16fcfb]" />
              </div>
              <Button type="submit" data-testid="forgot-password-submit-btn" disabled={submitting}
                className="w-full rounded-full h-12 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan">
                {submitting ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending</>) : "Send reset link"}
              </Button>
            </form>
          )}

          <p className="mt-6 text-center font-body text-sm text-white/55">
            Remembered it?{" "}
            <Link to="/login" className="text-[#16fcfb] hover:underline">Back to sign in</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
